/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
// utils
import {
    formatNumber,
    getChangeRateDirection,
    getEnabledRollUps,
    formatProductAnalyticsReportDates,
    formatProductAnalyticsTableDate,
    getChartColorsSubset
} from '../../../../../utils/charts';
// material
import CardToggles from '../../../../../material/cardwrapper/cardtoggles/cardtoggles';
import CardToggle from '../../../../../material/cardwrapper/cardtoggles/cardtoggle/cardtoggle';
import DetailsTable from '../../../../../material/detailstable/detailstable';
import BarChart from '../../../../../material/barchart/barchart';
import ChartLegend from '../../../../../material/chartlegend/chartlegend';
import ChartReportSection from '../../../../../material/chartreportsection/chartreportsection';
// styles
import injectSheet from 'react-jss';
import styles from './totalclicksreport_styles';
import LineChart from 'material/linechart/linechart';


const ARTICLE_VIEW_GENERATED_TITLE = 'Article Views Generated' // Total Clicks
const ARTICLE_VIEWS_UPTICK_TITLE = 'Article Views Uptick %' // Pending from BE/Data

/* ========== ~~~~~~~~~~ TRENDING NOW ANALYTICS - TOTAL CLICKS REPORT ~~~~~~~~~~ ========== */
const TotalClicksReport = injectSheet(styles)((props) => {

    // props
    const { selectedInterval, setSelectedInterval } = props;

    // redux state
    const tempTrendingNowConfig = useSelector(state => state.trendingNowV2.tempTrendingNowConfig)
    const { overview, details, status } = useSelector((state) => state.trendingNowV2.totalClicksReport);
    const { overview: pageViewRateOverview, details: pageViewRateDetails, status: pageViewRateStatus } = useSelector((state) => state.trendingNowV2.generatedPageViewsReport);

    const [selectedReport, setSelectedReport] = useState('article-views-uptick');

    const [showArticleViewsUptick, setShowArticleViewsUptick] = useState(false);
    const [isLoadingReport, loadReport] = useState(false)
    const timerRef = useRef(null);
    const { filters } = useSelector((state) => state.app);
    const  changeType = filters.compareTo;

    // component states
    const [articleViewsUptickLegends, setArticleViewsUptickLegends] = useState([]);
    const isShowArticleViewsUptick = selectedReport === 'article-views-uptick';

    const [chartLegends, setChartLegends] = useState([]);
    const [showTotal, setShowTotal] = useState(false);
    const [enabledRollups, setEnabledRollups] = useState([]);
    const [selectedPeriodX, setSelectedPeriodX] = useState(null);

    // component constants
    const chartTableColors = getChartColorsSubset(3)
    const totalDatesForInterval = details ? details[selectedInterval] : {};


    const products = [
      { id: 'trendingArticlesClicks', name: 'Trending Article', color: chartTableColors[0] },
      { id: 'featuredArticlesClicks', name: 'Featured Article', color: chartTableColors[1] },
      { id: 'promotedArticlesClicks', name: 'Promoted Article', color: chartTableColors[2] },
      { id: 'totalArticlesClicks', name: 'Total Article Clicks', color: '#fff'}
   ]

    const articleViewsUptickProducts = [{ id: 'pageViewRate', name: ARTICLE_VIEWS_UPTICK_TITLE, color: chartTableColors[0] }];

    const totalIndex = products.map(product => product.id).indexOf('totalArticlesClicks');


    const dates = details ? details[selectedInterval] : {};

    // show aggregate total on load of page
    useEffect(() => {
        if(status === 'fulfilled' && overview) showAggregateTotal();
    }, [overview]);

    // show article views uptick on load of page
    useEffect(() => {
        if(pageViewRateStatus === 'fulfilled' && pageViewRateOverview)  showArticleViewsUptickProductsTotal();
    }, [pageViewRateOverview]);

  // when mouse hover leaves the chart show back the net total aggregate
  useEffect(() => {
    if (showArticleViewsUptick) {
      if (isShowArticleViewsUptick) showArticleViewsUptickProductsTotal();
      else showAggregateTotal();
    }
  }, [showArticleViewsUptick]);



    useEffect(() => {
      // Clear the interval when the component unmounts
      return () => clearTimeout(timerRef.current);
    }, []);

    // disable/enable intervals based on the number of dates
    useEffect(() => {
        if(status === 'fulfilled' && enabledRollups.length === 0) {
            const rollups = getEnabledRollUps(details, 'trending');
            setEnabledRollups(rollups);
        } else {
            setEnabledRollups([]);
        }
    }, [status]);


    const computeTableTotal = (products, checkedOnly = true) => {
        // Compute total depending on the selected products
        const total = products.filter(product => product.id !== 'totalArticlesClicks' && (checkedOnly ? (product.checked === true) : true))
                            .map(product => product.value)
                            .reduce((prev, current) => prev + current, 0);
        return total;
    }

    const showAggregateTotal = () => {
        const productArr = products.map(product => ({
            ...product,
            value: Object.keys(overview).length > 0 ? overview.period[product.id] : 0,
            changeRate: Object.keys(overview).length > 0 ? overview.period[`${product.id}RateChange`] : 0,
            checked: chartLegends.length > 0 ? chartLegends.filter(legend => legend.id === product.id)[0].checked : true
        }))

        productArr[totalIndex] = {
            ...productArr[totalIndex],
            value: computeTableTotal(productArr)
        }

        setChartLegends(productArr);    
    };


    const renderBarChartLegends = () => {
        const totalValue = computeTableTotal(chartLegends, false);

        return chartLegends.map((legend, index) => {
            const productPercentage = totalValue > 0 ? ((legend.value ? legend.value : 0) / totalValue) * 100 : 0;
            const productPercentageText = totalValue ? '(' + formatNumber(productPercentage, 'percent') + ')' : '';
            return (
                <ChartLegend
                    title={`${
                        legend.id === 'total' ? ARTICLE_VIEW_GENERATED_TITLE : legend.name
                    } ${chartLegends.length > 2 && legend.id !== 'totalArticlesClicks' ? productPercentageText : ''}`}
                    checkboxColor={legend.color}
                    checked={legend.checked}
                    onCheck={checked => {
                        const legends = [...chartLegends];
                        legends[index] = {
                            ...legends[index],
                            checked: checked
                        }

                        legends[totalIndex] = {
                            ...legends[totalIndex],
                            value: computeTableTotal(legends)
                        }

                        setChartLegends(legends);
                    }}
                    value={formatNumber(legend.value, 'number')}
                    changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
                    change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
                    changeType={changeType}
                    metricType={'trending'}
                />
            )
        })        
    }

    const transformBarChartData = () => {
        let barChartData = [];
        barChartData = Object.keys(dates).map((date, index) => {
            const dateLength = Object.keys(dates).length;
            const formattedDate = formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true);
            let item = {date: formattedDate}
            products.forEach(product => {
                const detailsDateData = details[selectedInterval][date];
                const checkedProducts = chartLegends.filter(product => product.checked === true);
                if(checkedProducts.map(product => product.id).indexOf(product.id) >= 0) {
                    item[product.name] = detailsDateData[product.id];
                    item[`${product.name}Color`] = product.color;
                    
                    item['origDate'] = date;
                    item['formattedTableDate'] = formatProductAnalyticsTableDate(date, selectedInterval, detailsDateData.startDate, detailsDateData.endDate);
                }
            })
            return item;
        })
        return barChartData;
    }

    const handleHoverOnBarChart = barData => {
        const date = barData.data ? barData.data.origDate : barData.origDate;
        const legends = [...chartLegends];

        products.forEach(product => {
            let value = 0;
            let changeRate = 0;
            const productDetails = details[selectedInterval][date] ? details[selectedInterval][date] : null;
            if(productDetails) {
                value = productDetails[product.id] ? productDetails[product.id] : 0;
                changeRate = productDetails[`${product.id}RateChange`]  ? productDetails[`${product.id}RateChange`] : 0;
            }

            const legendIndex = chartLegends.map(legend => legend.id).indexOf(product.id);
            legends[legendIndex] = {
                ...legends[legendIndex],
                value,
                changeRate
            }
        })

        legends[totalIndex] = {
            ...legends[totalIndex],
            value: computeTableTotal(legends)
        }

        setChartLegends(legends);
        setSelectedPeriodX(`${barData.data ? barData.data.formattedTableDate : barData.formattedTableDate}`);
        setShowTotal(false);
        setShowArticleViewsUptick(false);
    }

      /**
   * show aggregate total for article views uptick
   */
  const showArticleViewsUptickProductsTotal = () => {
    const productArr = articleViewsUptickProducts.map((product) => ({
      ...product,
      value: Object.keys(pageViewRateOverview || []).length > 0 ? pageViewRateOverview.period[product.id] : 0,
      changeRate: Object.keys(pageViewRateOverview || []).length > 0 ? pageViewRateOverview.period[`${product.id}Change`] : 0,
      checked:
        articleViewsUptickLegends.length > 0
          ? articleViewsUptickLegends.filter((legend) => legend.id === product.id)[0].checked
          : true,
    }));
    setArticleViewsUptickLegends(productArr);
  };


    const transformArticleViewsUptickLineData = () => {
        const lineChartData = [];
        const dateLength = Object.keys(totalDatesForInterval).length;
        if (pageViewRateDetails && pageViewRateDetails[selectedInterval]) {
          const checkedProducts = articleViewsUptickLegends.filter((product) => product.checked === true);
          const items = [];
          articleViewsUptickProducts.forEach((product) => {
            if (checkedProducts.map((product) => product.id).indexOf(product.id) >= 0) {
              Object.keys(pageViewRateDetails[selectedInterval]).forEach((date, index) => {
                const value = pageViewRateDetails[selectedInterval][date].pageViewRate;
                // To allow duplicate x-axis labels
                let spaces = '';
                for (let i = 0; i < index; i++) spaces += ' ';
                const detailsDateData = pageViewRateDetails[selectedInterval][date];
                items.push({
                  x: formatProductAnalyticsReportDates(date, selectedInterval, dateLength, 10, true).concat(spaces),
                  y: value,
                  origDate: date,
                  formattedDate: formatProductAnalyticsTableDate(
                    date,
                    selectedInterval,
                    detailsDateData.startDate,
                    detailsDateData.endDate,
                  ),
                  productName: ARTICLE_VIEWS_UPTICK_TITLE,
                });
              });
    
              lineChartData.push({
                id: product.id,
                color: product.color,
                data: items,
              });
            }
          });
        }
        return lineChartData;
      };

      const renderArticleViewsUptickLineChartLegends = () => {
        return articleViewsUptickLegends.map((legend, index) => {
          return (
            <ChartLegend
              title={`${legend.name}`}
              checkboxColor={legend.color}
              checked={legend.checked}
              onCheck={(checked) => {
                const legends = [...articleViewsUptickLegends];
                legends[index] = {
                  ...legends[index],
                  checked: checked,
                };
                setArticleViewsUptickLegends(legends);
              }}
              value={formatNumber(legend.value, 'percent')}
              changeDirection={legend.changeRate < 0 ? 'negative' : 'positive'}
              change={formatNumber(legend.changeRate ? legend.changeRate : 0, 'percent')}
              changeType={changeType}
              metricType={'commenting'}
            />
          );
        });
      };
    


    const handleMouseLeave = () => {
        setShowTotal(true);
        setShowArticleViewsUptick(true)
        setSelectedPeriodX('');
    }

    const getBarChartKeys = () =>
        isShowArticleViewsUptick
          ? articleViewsUptickProducts.map((product) => product.name).reverse()
          : products.map((product) => product.name).reverse();
    
      const getBarChartColors = () =>
        isShowArticleViewsUptick
          ? articleViewsUptickProducts.map((product) => product.color).reverse()
          : products.map((product) => product.color).reverse();


    const totalClicksBarChart = {
        indexBy: 'date',
        data: isShowArticleViewsUptick ? transformArticleViewsUptickLineData() : transformBarChartData(),
        keys:  getBarChartKeys(),
        colors: getBarChartColors(),
        legends: isShowArticleViewsUptick ? renderArticleViewsUptickLineChartLegends() : renderBarChartLegends()
    }


    const handleSelectReport = (report) => {
        loadReport(true);
        setSelectedReport(report);
        timerRef.current = setTimeout(() => loadReport(false), 1500);
    };

    const cardtoggles = (
		<CardToggles>
            <CardToggle
                label={ARTICLE_VIEWS_UPTICK_TITLE}
                compareType={changeType}
                metricType={'trending'}
                percentChange={{
                    value: pageViewRateOverview?.period ? formatNumber(pageViewRateOverview?.period?.pageViewRate, 'percent') : 0,
                    direction: pageViewRateOverview?.period ? getChangeRateDirection(pageViewRateOverview?.period?.pageViewRate) : 'up'
                }}
                selected={selectedReport === 'article-views-uptick'}
                handleClick={() => handleSelectReport('article-views-uptick')}
                value={overview?.period ? formatNumber(pageViewRateOverview?.period?.pageViewRate, 'percent') : ''}
            />
            <CardToggle
                label={ARTICLE_VIEW_GENERATED_TITLE}
                compareType={changeType}
                metricType={'trending'}
                percentChange={{
                    value: overview?.period ? formatNumber(overview.period.totalArticlesClicks, 'percent') : 0,
                    direction: overview?.period ? getChangeRateDirection(overview.period.totalArticlesClicks) : 'up'
                }}
                selected={selectedReport === 'article-views-generated'}
                handleClick={() => handleSelectReport('article-views-generated')}

                value={overview?.period ? formatNumber(overview.period.totalArticlesClicks) : ''}
            />
		</CardToggles>
	);

    const barchart = (
        <BarChart
            indexBy={'date'}
            colors={totalClicksBarChart.colors}
            keys={totalClicksBarChart.keys}
            data={totalClicksBarChart.data}
            period={selectedInterval}
            loading={status === 'pending' || isLoadingReport ? true : false}
            onHover={data => handleHoverOnBarChart(data)}
            onMouseLeave={handleMouseLeave}
            reset={showTotal}
            barTotalKey={'Total Article Clicks'}
        />
    );

    const handleHoverArticleViewsUptickLineChart = (lineChartData) => {
        const date = lineChartData.data ? lineChartData.data.origDate : lineChartData.origDate;
        const legends = [...articleViewsUptickLegends];
        articleViewsUptickProducts.forEach((product) => {
          let value = 0;
          let changeRate = 0;
          const productDetails = pageViewRateDetails[selectedInterval][date] ? pageViewRateDetails[selectedInterval][date] : null;
          if (productDetails) {
            value = productDetails[product.id] ? productDetails[product.id] : 0;
            changeRate = productDetails[`${product.id}Change`] ? productDetails[`${product.id}Change`] : 0;
          }
    
          const legendIndex = articleViewsUptickLegends.map((product) => product.id).indexOf(product.id);
          legends[legendIndex] = {
            ...legends[legendIndex],
            value,
            changeRate,
          };
        });
        setArticleViewsUptickLegends(legends);
        setSelectedPeriodX(`${lineChartData.data ? lineChartData.data.formattedDate : lineChartData.formattedDate}`);
        setShowArticleViewsUptick(false);
      };

      
    const formattedData = transformArticleViewsUptickLineData();
    const hasSingleData = () => {
      let singleData = false;
      formattedData.forEach((item) => {
        if (item.data.length === 1) singleData = true;
      });
      return singleData;
    };

    const lineChart = (
        <LineChart
          category={'product'}
          enablePoints={hasSingleData()}
          data={formattedData}
          onHover={handleHoverArticleViewsUptickLineChart}
          onMouseLeave={handleMouseLeave}
          reset={showArticleViewsUptick}
          yType={'avg-in-view'}
          useDashedLines={true}
          period={selectedInterval}
          isPercentageData={true}
          loading={status === 'pending' || isLoadingReport ? true : false}
        />
      );
    

    const detailtable = status === 'fulfilled' ? (
        <DetailsTable
            period={selectedInterval}
            tableType={'trending'}
            products={isShowArticleViewsUptick ? articleViewsUptickProducts : products}
            data={isShowArticleViewsUptick ? (pageViewRateDetails ? pageViewRateDetails[selectedInterval] : null) : (details ? details[selectedInterval] : null)}
            csvFileName={isShowArticleViewsUptick ? ARTICLE_VIEWS_UPTICK_TITLE : ARTICLE_VIEW_GENERATED_TITLE}
            dataType={isShowArticleViewsUptick ? 'percent' : 'number'}
        />
    ) : []

    const isNoData = () => {
        return  (status === 'rejected') ||
                (status === 'fulfilled' && !overview?.period?.totalArticlesClicks) ||
                (status === 'fulfilled' && Object.keys(overview).length === 0);
    }

    const tooltip = useMemo(() => {
      if(selectedReport === 'article-views-uptick') 
        return {
          title: ARTICLE_VIEWS_UPTICK_TITLE,
          content: `Displays the percentage increase in article views attributed to the presence of the Trending Now unit on the page.`
        }
      if(selectedReport === 'article-views-generated') 
        return {
          title: ARTICLE_VIEW_GENERATED_TITLE,
          content: `Indicates the total number of additional article views resulting from user clicks on a ‘popular’ article in the Trending Now unit.`
        }
    }, [selectedReport])

    return (
        <div name={'totalclicksreport'} class={`${props.classes.totalclicksreport}`} >
            <ChartReportSection
                legendTitle={selectedPeriodX}
                title={ARTICLE_VIEW_GENERATED_TITLE}
                tooltipContent={
                    <div className={'tip-content'}>
                        <div className={'tip-title'}>
                            <h4>{tooltip.title}</h4>
                        </div>
                        <div className={'tip-desc'}>
                            <p>{tooltip.content}</p>
                        </div>
                    </div>
                }
                cardtoggles={cardtoggles}
                chart={isShowArticleViewsUptick ? lineChart : barchart}
                // loading={status === 'pending' ? true : false}
                noData={isNoData()}
                enabledPeriods={enabledRollups}
                chartLegend={totalClicksBarChart.legends}
                handleSelectPeriod={period => {
                    setSelectedInterval(period);
                    handleMouseLeave();
                }}
                selectedPeriod={selectedInterval}
                detailTable={detailtable}
            />
        </div>
    );
});


/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default TotalClicksReport;
